import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { Truck, Search, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import Spinner from './Spinner.js';

const PickupChallanList = ({ db, user }) => {
  const [loading, setLoading] = useState(true);
  const [pickups, setPickups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const isReadOnly = user?.role === 'accounts';
  const detailBasePath = isReadOnly ? '/accounts/pickup-challan' : '/ops/pickup-challan';

  useEffect(() => {
    // Fetch from unified 'inwards' collection
    const q = query(
      collection(db, "inwards"),
      orderBy("createdAt", "desc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs
        .filter(doc => {
          const entry = doc.data();
          const status = entry.status;
          if (status === 'qc_completed') return false;
          // Show pickup flow records through MIC and QC handoff, as it's the same unified document.
          const isPickupFlowStatus =
            status === 'pc_draft' ||
            status === 'pc_ready' ||
            status === 'pc_completed' ||
            status === 'mic_draft' ||
            status === 'mic_ready' ||
            status === 'mic_completed' ||
            status === 'qc_pending';
          const isInterbranchMic = entry.typeOfMIC === 'Interbranch Transfer';
          return isPickupFlowStatus && !isInterbranchMic;
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      setPickups(data);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching inwards:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this record? This action cannot be undone.")) {
      try {
        await deleteDoc(doc(db, "inwards", id));
      } catch (error) {
        console.error("Error deleting document: ", error);
        alert("Failed to delete the record.");
      }
    }
  };

  const filteredPickups = pickups.filter(p => 
    p.inwardNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.deliveryChallanNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const statusMap = {
      'pc_draft': { label: 'Draft', bg: '#f1f5f9', color: '#475569' },
      'pc_ready': { label: 'Ready', bg: '#eff6ff', color: '#2563eb' },
      'pc_completed': { label: 'Completed', bg: '#f0fdf4', color: '#166534' },
      'mic_draft': { label: 'MIC Draft', bg: '#f0fdf4', color: '#166534' },
      'mic_ready': { label: 'MIC Ready', bg: '#fdf2f2', color: '#dc2626' },
      'mic_completed': { label: 'MIC Completed', bg: '#dcfce7', color: '#15803d' },
      'qc_pending': { label: 'QC Pending', bg: '#eef2ff', color: '#4338ca' },
      'qc_draft': { label: 'QC Draft', bg: '#f1f5f9', color: '#475569' },
      'qc_ready': { label: 'QC Ready', bg: '#eff6ff', color: '#2563eb' },
      'qc_completed': { label: 'QC Completed', bg: '#dcfce7', color: '#15803d' }
    };

    const config = statusMap[status] || { label: status?.toUpperCase().replace('_', ' ') || 'Draft', bg: '#f3f4f6', color: '#64748b' };

    return (
      <span style={{ 
        padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '800',
        textTransform: 'uppercase',
        backgroundColor: config.bg, 
        color: config.color,
        border: `1px solid ${config.color}20`
      }}>
        {config.label}
      </span>
    );
  };

  const tableHeaderStyle = { padding: '12px 15px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb' };
  const tableCellStyle = { padding: '12px 15px', fontSize: '14px', color: '#1e293b', borderBottom: '1px solid #e5e7eb' };

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Truck size={24} color="#2563eb" /> Pickup Engine
          </h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Unified tracking of site pickups and material returns.</p>
        </div>
        {!isReadOnly && (
          <button 
            onClick={() => navigate('/ops/pickup-challan/new')}
            style={{ backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={18} /> Initiate Pickup
          </button>
        )}
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <Search size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search by Inward No, Customer, or DC..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #d1d5db', outline: 'none', fontSize: '14px' }}
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={tableHeaderStyle}>PC No.</th>
                <th style={tableHeaderStyle}>PC Date</th>
                <th style={tableHeaderStyle}>Customer Name</th>
                <th style={tableHeaderStyle}>Reference Doc</th>
                <th style={tableHeaderStyle}>Document Type</th>
                <th style={tableHeaderStyle}>Pickup Agent</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPickups.length === 0 ? (
                <tr><td colSpan="8" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No records found in the engine.</td></tr>
              ) : (
                filteredPickups.map((p) => (
                  <tr 
                    key={p.id} 
                    onClick={() => navigate(`${detailBasePath}/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={tableCellStyle}>
                      <span style={{ fontWeight: '600', color: '#2563eb' }}>#{p.inwardNo}</span>
                    </td>
                    <td style={tableCellStyle}>{p.createdAt?.toDate ? p.createdAt.toDate().toLocaleDateString('en-IN') : 'N/A'}</td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '600' }}>{p.customerName || 'N/A'}</div>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '500' }}>{p.deliveryChallanNo || p.referenceNo || 'N/A'}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{p.dcDate || ''}</div>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{ fontSize: '12px', fontWeight: '500', color: '#64748b' }}>{p.type || 'N/A'}</span>
                    </td>
                    <td style={tableCellStyle}>{p.pickupAgent || 'N/A'}</td>
                    <td style={tableCellStyle}>{getStatusBadge(p.status)}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                       <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                          <button 
                            onClick={(e) => { e.stopPropagation(); navigate(`${detailBasePath}/${p.id}`); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: '#64748b' }}
                            title={!isReadOnly && p.status === 'pc_draft' ? "Edit" : "View"}
                          >
                            {!isReadOnly && p.status === 'pc_draft' ? <Edit2 size={16} /> : <Eye size={16} />}
                          </button>
                          {!isReadOnly && p.status === 'pc_draft' && (
                            <button 
                              onClick={(e) => handleDelete(e, p.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: '#ef4444' }}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                       </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PickupChallanList;
